import { abrirNombre, clavePublica, nombrePrivado, nuevasLlaves, sellarNombre, type NombreSellado } from './sesion-canal-privado';
export const CANAL_SESION = 'kapi-sesion-pestanas';
export const SIN_HERENCIA = 'kapi:sesion:sin-herencia';
export const SESION_CAMBIADA = 'kapi:sesion-version';
export const MARCA_SALIDA = 'kapi:sesion:salida';
export function leerMarcaSalida(w: Window): string | null {
  try { return w.localStorage.getItem(MARCA_SALIDA); } catch { return null; }
}
const FOCO = 'kapi:sesion:ultimo-foco';
const privadas = new Set([FOCO, SIN_HERENCIA, 'kapi:guardia:ultima']);
interface Direccion { peticion: string; origen: string; destino?: string; oferta?: string }
interface Mensaje extends Direccion {
  tipo: 'pedir' | 'ofrecer' | 'elegir' | 'cancelar';
  foco?: number;
  publica?: JsonWebKey;
  sellado?: NombreSellado;
}
interface Entrega extends Direccion {
  tipo: 'copiar' | 'recibido' | 'confirmar' | 'finalizado' | 'cancelar';
  copia?: Record<string, string>;
}
interface Oferta {
  id: string; destino: string; sesion: string; version: number; salida: string | null; vence: number; timer: number;
  llaves: CryptoKeyPair; publica: JsonWebKey; canal?: BroadcastChannel;
  fase: 'ofrecida' | 'elegida' | 'copiada' | 'confirmada';
}
interface Pendiente {
  id: string; vence: number; salida: string | null; ofertas: Map<string, Mensaje>; canceladas: Set<string>; elegido?: Mensaje;
  canal?: BroadcastChannel; copia?: Record<string, string>; terminar(ok: boolean): void;
}
export function crearIntercambio(w: Window, crearCanal: (nombre: string) => BroadcastChannel = nombre => new BroadcastChannel(nombre)) {
  const id = crypto.randomUUID(), s = w.sessionStorage;
  let canal: BroadcastChannel | null = null, pendiente: Pendiente | null = null;
  let arranque: Promise<boolean> | null = null, detenido = false, version = 0;
  const ofrecidas = new Map<string, Oferta>();
  const sesion = () => {
    try {
      const token = s.getItem('kapi:access_token'), user = s.getItem('kapi:user'), branch = s.getItem('kapi:selected_branch');
      return token && JSON.parse(user || 'null')?.id && branch ? JSON.stringify([token, user, branch]) : null;
    } catch { return null; }
  };
  const enviar = (m: Omit<Mensaje, 'origen'>) => canal?.postMessage({ ...m, origen: id });
  const vigente = (peticion: string, o: Oferta) => !detenido && ofrecidas.get(peticion) === o && Date.now() < o.vence
    && o.salida === leerMarcaSalida(w) && !s.getItem(SIN_HERENCIA) && o.version === version && o.sesion === sesion();
  const esperando = (p: Pendiente) => !detenido && pendiente === p && Date.now() < p.vence
    && p.salida === leerMarcaSalida(w) && !s.getItem('kapi:access_token') && !s.getItem(SIN_HERENCIA);
  const cerrarOferta = (peticion: string, o: Oferta, cancelar = true) => {
    if (ofrecidas.get(peticion) !== o) return;
    if (cancelar) {
      enviar({ tipo: 'cancelar', peticion, destino: o.destino, oferta: o.id });
      o.canal?.postMessage({ tipo: 'cancelar', peticion, origen: id, destino: o.destino, oferta: o.id });
    }
    o.canal?.close(); w.clearTimeout(o.timer); ofrecidas.delete(peticion);
  };
  const anunciar = (peticion: string, o: Oferta) => {
    if (!vigente(peticion, o)) { cerrarOferta(peticion, o); return; }
    enviar({ tipo: 'ofrecer', peticion, destino: o.destino, oferta: o.id, publica: o.publica, foco: Number(s.getItem(FOCO)) || 0 });
  };
  const foco = () => {
    if (w.document.visibilityState === 'hidden') return;
    s.setItem(FOCO, String(Date.now()));
    for (const [peticion, o] of ofrecidas) if (o.fase === 'ofrecida') anunciar(peticion, o);
  };
  const cambio = () => { version++; for (const [peticion, o] of ofrecidas) cerrarOferta(peticion, o); };
  const cancelar = () => { s.setItem(SIN_HERENCIA, '1'); pendiente?.terminar(false); cambio(); };
  w.addEventListener('focus', foco);
  w.document.addEventListener('visibilitychange', foco);
  w.addEventListener('kapi:sesion-borrada', cancelar);
  w.addEventListener(SESION_CAMBIADA, cambio);
  if (w.document.hasFocus()) foco();

  const ofrecer = async (data: Mensaje) => {
    const inicial = sesion(), v = version, salida = leerMarcaSalida(w), vence = Date.now() + 350;
    if (!inicial || s.getItem(SIN_HERENCIA) || ofrecidas.has(data.peticion)) return;
    const llaves = await nuevasLlaves(), publica = await clavePublica(llaves);
    // Criptografía asíncrona: la sesión pudo cambiar mientras se generaba la clave.
    if (detenido || salida !== leerMarcaSalida(w) || version !== v || sesion() !== inicial || s.getItem(SIN_HERENCIA) || Date.now() >= vence || ofrecidas.has(data.peticion)) return;
    const o: Oferta = { id: crypto.randomUUID(), destino: data.origen, sesion: inicial, version: v, salida, vence, llaves, publica,
      fase: 'ofrecida', timer: w.setTimeout(() => cerrarOferta(data.peticion, o), vence - Date.now()) };
    ofrecidas.set(data.peticion, o); anunciar(data.peticion, o);
  };
  const entregar = async (data: Mensaje) => {
    const o = ofrecidas.get(data.peticion);
    if (!o || o.id !== data.oferta || o.destino !== data.origen || o.fase !== 'ofrecida') return;
    if (!vigente(data.peticion, o) || !data.sellado) { cerrarOferta(data.peticion, o); return; }
    o.fase = 'elegida';
    try {
      const nombre = await abrirNombre(data.sellado, o.llaves.privateKey, JSON.stringify([data.peticion, o.id, id, data.origen]));
      if (!vigente(data.peticion, o)) { cerrarOferta(data.peticion, o); return; }
      const privado = crearCanal(nombre); o.canal = privado;
      const mandar = (tipo: Entrega['tipo'], copia?: Record<string, string>) => privado.postMessage({ tipo, copia, peticion: data.peticion, oferta: o.id, origen: id, destino: o.destino });
      privado.onmessage = ({ data: m }: MessageEvent<Entrega>) => {
        if (!m || m.peticion !== data.peticion || m.oferta !== o.id || m.origen !== o.destino || m.destino !== id) return;
        if (!vigente(data.peticion, o)) { cerrarOferta(data.peticion, o); return; }
        if (m.tipo === 'cancelar' || (m.tipo === 'finalizado' && o.fase === 'confirmada')) { cerrarOferta(data.peticion, o, false); return; }
        if (m.tipo === 'recibido' && o.fase === 'copiada') { o.fase = 'confirmada'; mandar('confirmar'); }
      };
      const copia: Record<string, string> = {};
      for (let i = 0; i < s.length; i++) { const k = s.key(i)!; if (!privadas.has(k)) copia[k] = s.getItem(k)!; }
      o.fase = 'copiada'; mandar('copiar', copia);
    } catch { cerrarOferta(data.peticion, o); }
  };
  const elegir = async (p: Pendiente) => {
    if (!esperando(p)) { p.terminar(false); return; }
    const mejor = [...p.ofertas.values()].sort((a, b) => b.foco! - a.foco! || b.origen.localeCompare(a.origen))[0];
    if (!mejor?.publica) { p.terminar(false); return; }
    p.elegido = mejor;
    try {
      const nombre = nombrePrivado(), privado = crearCanal(nombre); p.canal = privado;
      const mandar = (tipo: Entrega['tipo']) => privado.postMessage({ tipo, peticion: p.id, oferta: mejor.oferta, origen: id, destino: mejor.origen });
      privado.onmessage = ({ data: m }: MessageEvent<Entrega>) => {
        if (!esperando(p)) { p.terminar(false); return; }
        if (!m || m.peticion !== p.id || m.oferta !== mejor.oferta || m.origen !== mejor.origen || m.destino !== id) return;
        if (m.tipo === 'cancelar') { p.terminar(false); return; }
        if (m.tipo === 'copiar' && !p.copia && m.copia) {
          const c = m.copia;
          try {
            if (!c['kapi:access_token'] || !JSON.parse(c['kapi:user'] || 'null')?.id || !c['kapi:selected_branch']) { p.terminar(false); return; }
            // No escribir un token en tránsito: la donante debe seguir viva al recibir este acuse.
            p.copia = c; mandar('recibido');
          } catch { p.terminar(false); }
        } else if (m.tipo === 'confirmar' && p.copia) {
          const anteriores = new Map<string, string | null>();
          try {
            for (const [k, v] of Object.entries(p.copia)) if (!privadas.has(k) && typeof v === 'string') { anteriores.set(k, s.getItem(k)); s.setItem(k, v); }
            mandar('finalizado'); foco(); p.terminar(true);
          } catch {
            for (const [k, v] of anteriores) { if (v === null) s.removeItem(k); else s.setItem(k, v); }
            p.terminar(false);
          }
        }
      };
      const sellado = await sellarNombre(nombre, mejor.publica, JSON.stringify([p.id, mejor.oferta, mejor.origen, id]));
      if (!esperando(p)) { p.terminar(false); return; }
      enviar({ tipo: 'elegir', peticion: p.id, destino: mejor.origen, oferta: mejor.oferta, sellado });
    } catch { p.terminar(false); }
  };
  try {
    canal = crearCanal(CANAL_SESION);
    canal.onmessage = ({ data }: MessageEvent<Mensaje>) => {
      if (!data || data.origen === id || typeof data.peticion !== 'string' || typeof data.origen !== 'string') return;
      if (data.tipo === 'pedir') { void ofrecer(data).catch(() => {}); return; }
      if (data.destino !== id) return;
      if (data.tipo === 'elegir') { void entregar(data); return; }
      if (data.tipo === 'cancelar') {
        const o = ofrecidas.get(data.peticion);
        if (o && o.id === data.oferta && o.destino === data.origen) cerrarOferta(data.peticion, o, false);
      }
      const p = pendiente;
      if (!p || p.id !== data.peticion || !esperando(p) || !data.oferta) return;
      if (data.tipo === 'ofrecer' && !p.elegido && !p.canceladas.has(data.oferta) && Number.isFinite(data.foco) && data.publica) p.ofertas.set(data.origen, data);
      if (data.tipo === 'cancelar') {
        p.canceladas.add(data.oferta);
        if (p.ofertas.get(data.origen)?.oferta === data.oferta) p.ofertas.delete(data.origen);
        if (p.elegido?.origen === data.origen && p.elegido.oferta === data.oferta) p.terminar(false);
      }
    };
  } catch { /* Sin canal, el arranque conserva el login normal. */ }
  return {
    preparar(): Promise<boolean> {
      if (s.getItem('kapi:access_token')) return Promise.resolve(!!sesion());
      if (detenido || s.getItem(SIN_HERENCIA) || !canal) return Promise.resolve(false);
      if (arranque) return arranque;
      arranque = new Promise<boolean>(resolve => {
        const peticion = crypto.randomUUID();
        const fin = w.setTimeout(() => p.terminar(false), 300);
        const eleccion = w.setTimeout(() => { void elegir(p); }, 200);
        const p: Pendiente = { id: peticion, vence: Date.now() + 300, salida: leerMarcaSalida(w), ofertas: new Map(), canceladas: new Set(), terminar(ok) {
          if (pendiente !== p) return;
          if (!ok && p.elegido) enviar({ tipo: 'cancelar', peticion, destino: p.elegido.origen, oferta: p.elegido.oferta });
          w.clearTimeout(fin); w.clearTimeout(eleccion); p.canal?.close(); p.copia = undefined; pendiente = null; resolve(ok);
        } };
        pendiente = p; enviar({ tipo: 'pedir', peticion });
      });
      return arranque;
    },
    detener() {
      pendiente?.terminar(false); cambio(); detenido = true; canal?.close();
      w.removeEventListener('focus', foco); w.document.removeEventListener('visibilitychange', foco);
      w.removeEventListener('kapi:sesion-borrada', cancelar); w.removeEventListener(SESION_CAMBIADA, cambio);
    },
  };
}
let intercambio: ReturnType<typeof crearIntercambio> | undefined;
export function prepararSesionPestana(): Promise<boolean> {
  try { intercambio ??= crearIntercambio(window); return intercambio.preparar(); }
  catch { return Promise.resolve(false); }
}
export function impedirHerencia(): void {
  try { sessionStorage.setItem(SIN_HERENCIA, '1'); } catch { /* almacenamiento no disponible */ }
  window.dispatchEvent(new Event('kapi:sesion-borrada'));
}
export function permitirHerencia(): void {
  try { sessionStorage.removeItem(SIN_HERENCIA); } catch { /* almacenamiento no disponible */ }
}
